import React from "react";
import {AppStore} from "./AppStore";
import {DisposeUtils} from "../utils/DisposeUtils";
import {ErrorUtils} from "../utils/ErrorUtils";
import {proto} from "../proto/messages";
import {makeAutoObservable, reaction, runInAction} from "mobx";
import {lessons} from "../proto/compiled";
import {MobxUtils} from "../utils/MobxUtils";
import {UploadStore} from "./UploadStore";

export const LessonEditorStoreContext = React.createContext<LessonEditorStore | undefined>(undefined);

export class LessonEditorStore {
  private disposer = new DisposeUtils();
  private app: AppStore;
  private loadPending = false;

  lessonId?: string;
  loading = false;
  saving = false;
  lesson?: lessons.ILessonDetail;

  // Editing state
  editingChapterId?: string;
  editingBlockId?: string;

  // Thumbnail upload
  thumbnailFileId?: string;

  // Progress (0-1) of an image / attachment upload from a text block, undefined when idle
  assetUploadProgress?: number;

  // Annotation selector state
  annotationSelectorOpen = false;
  annotationSelectorBlockId?: string;
  publishedAnnotations?: lessons.IVideoExample[];
  annotationsLoading = false;
  annotationsSearchQuery = "";

  constructor(app: AppStore, lessonId?: string) {
    this.app = app;
    this.lessonId = lessonId;
    makeAutoObservable(this);
    if (lessonId) {
      this.load();
    }
  }

  get statistics() {
    if (!this.lesson) return null;
    return {
      chapters: this.lesson.chapterCount ?? 0,
      videoExamples: this.lesson.videoExampleCount ?? 0,
      durationMinutes: Math.round((this.lesson.totalDurationSeconds ?? 0) / 60),
    };
  }

  dispose() {
    this.disposer.dispose();
  }

  // === Lesson Operations ===

  async load() {
    if (!this.lessonId) return;
    if (this.loading) {
      this.loadPending = true;
      return;
    }
    this.loading = true;
    await this.app.socketApiConnectedAndAuthenticated;
    try {
      const response = await this.app.socketApi.fetch(
        proto.TxGetLesson.create({id: this.lessonId}),
        new proto.RxGetLessonResponse(),
      );
      runInAction(() => {
        this.lesson = response.proto?.lesson ?? undefined;
        if (this.lesson) {
          MobxUtils.makeObservable(this.lesson);
          this.lesson.chapters?.forEach(chapter => {
            MobxUtils.makeObservable(chapter);
            chapter.contentBlocks?.forEach(block => MobxUtils.makeObservable(block));
          });
          // Initialize thumbnail file ID from loaded lesson
          this.thumbnailFileId = this.lesson.thumbnailFileId ?? undefined;
        }
      });
    } catch (e) {
      ErrorUtils.fromFetch(e);
    }
    runInAction(() => {
      this.loading = false;
      if (this.loadPending) {
        this.loadPending = false;
        this.load();
      }
    });
  }

  async createLesson(title: string, description: string, thumbnailFileId?: string): Promise<string | undefined> {
    this.saving = true;
    await this.app.socketApiConnectedAndAuthenticated;
    try {
      const response = await this.app.socketApi.fetch(
        proto.TxCreateLesson.create({
          title,
          description,
          thumbnailFileId: thumbnailFileId ?? "",
        }),
        new proto.RxCreateLessonResponse(),
      );
      runInAction(() => {
        this.saving = false;
        this.lessonId = response.proto?.lessonId ?? undefined;
      });
      return response.proto?.lessonId ?? undefined;
    } catch (e) {
      ErrorUtils.fromFetch(e);
      runInAction(() => {
        this.saving = false;
      });
      return undefined;
    }
  }

  async updateLesson() {
    if (!this.lesson || !this.lessonId) return;
    this.saving = true;
    await this.app.socketApiConnectedAndAuthenticated;
    try {
      const status = await this.app.socketApi.sendMessage(
        proto.TxUpdateLesson.create({
          id: this.lessonId,
          title: this.lesson.title ?? "",
          description: this.lesson.description ?? "",
          published: this.lesson.published ?? false,
          thumbnailFileId: this.thumbnailFileId ?? "",
        }),
        {ack: true}
      );
      if (!ErrorUtils.fromFetch(status)) {
        runInAction(() => {
          this.saving = false;
        });
        return;
      }
    } catch (e) {
      ErrorUtils.fromFetch(e);
    }
    runInAction(() => {
      this.saving = false;
    });
  }

  async deleteLesson(): Promise<boolean> {
    if (!this.lessonId) return false;
    this.saving = true;
    await this.app.socketApiConnectedAndAuthenticated;
    try {
      const status = await this.app.socketApi.sendMessage(
        proto.TxDeleteLesson.create({id: this.lessonId}),
        {ack: true}
      );
      runInAction(() => {
        this.saving = false;
      });
      return ErrorUtils.fromFetch(status) !== false;
    } catch (e) {
      ErrorUtils.fromFetch(e);
      runInAction(() => {
        this.saving = false;
      });
      return false;
    }
  }

  // === Chapter Operations ===

  async createChapter(title: string, atOrder?: number): Promise<lessons.ILessonChapter | undefined> {
    if (!this.lessonId) return undefined;
    this.saving = true;
    await this.app.socketApiConnectedAndAuthenticated;
    try {
      const order = atOrder ?? (this.lesson?.chapters?.length ?? 0);
      const response = await this.app.socketApi.fetch(
        proto.TxCreateChapter.create({
          lessonId: this.lessonId,
          title,
          order,
        }),
        new proto.RxCreateChapterResponse(),
      );
      const chapter = response.proto?.chapter;
      if (chapter && this.lesson) {
        runInAction(() => {
          if (!this.lesson!.chapters) {
            this.lesson!.chapters = [];
          }
          // Shift orders of existing chapters at or after the new position
          for (const existingChapter of this.lesson!.chapters) {
            if ((existingChapter.order ?? 0) >= order) {
              existingChapter.order = (existingChapter.order ?? 0) + 1;
            }
          }
          MobxUtils.makeObservable(chapter);
          this.lesson!.chapters.push(chapter);
          this.lesson!.chapters.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
          this.lesson!.chapterCount = this.lesson!.chapters.length;
        });
      }
      runInAction(() => {
        this.saving = false;
      });
      return chapter ?? undefined;
    } catch (e) {
      ErrorUtils.fromFetch(e);
      runInAction(() => {
        this.saving = false;
      });
      return undefined;
    }
  }

  async updateChapter(chapterId: string, title: string, order?: number) {
    const chapter = this.lesson?.chapters?.find(c => c.id === chapterId);
    if (!chapter) return;

    this.saving = true;
    await this.app.socketApiConnectedAndAuthenticated;
    try {
      const status = await this.app.socketApi.sendMessage(
        proto.TxUpdateChapter.create({
          id: chapterId,
          title,
          order: order ?? chapter.order ?? 0,
        }),
        {ack: true}
      );
      if (ErrorUtils.fromFetch(status) !== false) {
        runInAction(() => {
          chapter.title = title;
          if (order !== undefined) {
            chapter.order = order;
          }
        });
      }
    } catch (e) {
      ErrorUtils.fromFetch(e);
    }
    runInAction(() => {
      this.saving = false;
    });
  }

  async deleteChapter(chapterId: string): Promise<boolean> {
    this.saving = true;
    await this.app.socketApiConnectedAndAuthenticated;
    try {
      const status = await this.app.socketApi.sendMessage(
        proto.TxDeleteChapter.create({id: chapterId}),
        {ack: true}
      );
      if (ErrorUtils.fromFetch(status) !== false) {
        runInAction(() => {
          if (this.lesson?.chapters) {
            this.lesson.chapters = this.lesson.chapters.filter(c => c.id !== chapterId);
            // Recalculate order values to be sequential
            this.lesson.chapters.forEach((chapter, index) => {
              chapter.order = index;
            });
            this.lesson.chapterCount = this.lesson.chapters.length;
          }
        });
        runInAction(() => {
          this.saving = false;
        });
        return true;
      }
    } catch (e) {
      ErrorUtils.fromFetch(e);
    }
    runInAction(() => {
      this.saving = false;
    });
    return false;
  }

  // === Content Block Operations ===

  async createContentBlock(
    chapterId: string,
    blockType: lessons.ContentBlockType,
    afterBlockId?: string
  ): Promise<lessons.IContentBlock | undefined> {
    const chapter = this.lesson?.chapters?.find(c => c.id === chapterId);
    if (!chapter) return undefined;

    this.saving = true;
    await this.app.socketApiConnectedAndAuthenticated;
    try {
      // Calculate order - insert after specified block or at end
      let order = 0;
      if (afterBlockId) {
        const afterBlock = chapter.contentBlocks?.find(b => b.id === afterBlockId);
        order = (afterBlock?.order ?? -1) + 1;
      } else {
        order = chapter.contentBlocks?.length ?? 0;
      }

      const response = await this.app.socketApi.fetch(
        proto.TxCreateContentBlock.create({
          chapterId,
          order,
          blockType,
        }),
        new proto.RxCreateContentBlockResponse(),
      );

      const block = response.proto?.block;
      if (block) {
        runInAction(() => {
          if (!chapter.contentBlocks) {
            chapter.contentBlocks = [];
          }
          MobxUtils.makeObservable(block);
          // Shift orders of blocks at or after the new block's position
          const newOrder = block.order ?? 0;
          for (const existingBlock of chapter.contentBlocks) {
            if ((existingBlock.order ?? 0) >= newOrder) {
              existingBlock.order = (existingBlock.order ?? 0) + 1;
            }
          }
          // Insert and sort
          chapter.contentBlocks.push(block);
          chapter.contentBlocks.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
          this.editingBlockId = block.id ?? undefined;
        });
      }
      runInAction(() => {
        this.saving = false;
      });
      return block ?? undefined;
    } catch (e) {
      ErrorUtils.fromFetch(e);
      runInAction(() => {
        this.saving = false;
      });
      return undefined;
    }
  }

  async updateContentBlock(block: lessons.IContentBlock) {
    if (!block.id) return;
    this.saving = true;
    await this.app.socketApiConnectedAndAuthenticated;
    try {
      const status = await this.app.socketApi.sendMessage(
        proto.TxUpdateContentBlock.create({
          id: block.id,
          order: block.order ?? 0,
          richText: block.richText ?? "",
          textStyle: block.textStyle ?? lessons.TextBlockStyle.TEXT_STYLE_NORMAL,
          annotationId: block.videoExample?.annotation?.id ?? "",
        }),
        {ack: true}
      );
      ErrorUtils.fromFetch(status);
    } catch (e) {
      ErrorUtils.fromFetch(e);
    }
    runInAction(() => {
      this.saving = false;
    });
  }

  async deleteContentBlock(chapterId: string, blockId: string): Promise<boolean> {
    const chapter = this.lesson?.chapters?.find(c => c.id === chapterId);
    if (!chapter) return false;

    this.saving = true;
    await this.app.socketApiConnectedAndAuthenticated;
    try {
      const status = await this.app.socketApi.sendMessage(
        proto.TxDeleteContentBlock.create({id: blockId}),
        {ack: true}
      );
      if (ErrorUtils.fromFetch(status) !== false) {
        runInAction(() => {
          if (chapter.contentBlocks) {
            chapter.contentBlocks = chapter.contentBlocks.filter(b => b.id !== blockId);
            // Recalculate order values to be sequential
            chapter.contentBlocks.forEach((block, index) => {
              block.order = index;
            });
          }
          if (this.editingBlockId === blockId) {
            this.editingBlockId = undefined;
          }
        });
        runInAction(() => {
          this.saving = false;
        });
        return true;
      }
    } catch (e) {
      ErrorUtils.fromFetch(e);
    }
    runInAction(() => {
      this.saving = false;
    });
    return false;
  }

  // === Assets ===

  // Uploads an image or attachment referenced from rich text and attaches it to the lesson.
  // Resolves to the file url, or undefined on failure (errors are already surfaced).
  async uploadAsset(file: File): Promise<string | undefined> {
    if (!this.lessonId) return undefined;
    const upload = new UploadStore(this.app);
    const disposeProgress = reaction(
      () => upload.progress,
      progress => runInAction(() => this.assetUploadProgress = progress ?? 0),
    );
    this.assetUploadProgress = 0;
    try {
      const uploaded = await upload.uploadFile([file]);
      if (!uploaded?.id || !uploaded.src) return undefined;
      const status = await this.app.socketApi.sendMessage(
        proto.TxAddLessonAsset.create({lessonId: this.lessonId, fileId: uploaded.id}),
        {ack: true}
      );
      return ErrorUtils.fromFetch(status) === false ? undefined : uploaded.src;
    } finally {
      disposeProgress();
      upload.dispose();
      runInAction(() => this.assetUploadProgress = undefined);
    }
  }

  // === Annotation Selector ===

  openAnnotationSelector(blockId: string) {
    this.annotationSelectorBlockId = blockId;
    this.annotationSelectorOpen = true;
    this.loadPublishedAnnotations();
  }

  closeAnnotationSelector() {
    this.annotationSelectorOpen = false;
    this.annotationSelectorBlockId = undefined;
  }

  async loadPublishedAnnotations(search?: string) {
    this.annotationsLoading = true;
    this.annotationsSearchQuery = search ?? "";
    await this.app.socketApiConnectedAndAuthenticated;
    try {
      const response = await this.app.socketApi.fetch(
        proto.TxListPublishedAnnotations.create({
          searchQuery: search ?? "",
        }),
        new proto.RxListPublishedAnnotationsResponse(),
      );
      runInAction(() => {
        this.publishedAnnotations = response.proto?.annotations ?? [];
        this.annotationsLoading = false;
      });
    } catch (e) {
      ErrorUtils.fromFetch(e);
      runInAction(() => {
        this.annotationsLoading = false;
      });
    }
  }

  async linkAnnotation(blockId: string, annotationId: string) {
    // Find the block and chapter
    for (const chapter of this.lesson?.chapters ?? []) {
      const block = chapter.contentBlocks?.find(b => b.id === blockId);
      if (block) {
        // Find the annotation from loaded list
        const annotation = this.publishedAnnotations?.find(
          a => a.annotation?.id === annotationId
        );
        if (annotation) {
          block.videoExample = annotation;
          await this.updateContentBlock(block);
        }
        break;
      }
    }
    this.closeAnnotationSelector();
  }
}
